import React, { useMemo, useEffect, useState, useRef, forwardRef } from 'react';
import AgGridReact from './AgGrid/AgGridReact.jsx';
import { gridThemeNoBorder } from './AgGrid/gridTheme.js';
import { fetchJson } from '../../helpers/fetch.js';
import { JSONtoArr } from '../datagrid/hooks/useServerSideData.jsx';
import useRowId from '../datagrid/hooks/useRowId.js';
import {commonGridOptions, DATA_TYPE_DEFINITIONS, getLocaleText} from './AgGrid/index.jsx';
import LoadingIndicator from "./loading_indicator";


export function usePaginatedSSR({fetchPath, fetchBody, onResponse, onSuccess}) {
  return () => {
    return {
      getRows: (params) => {
        const { startRow, endRow } = params.request;
        const perPage = endRow - startRow;
        const page = Math.floor(startRow / perPage) + 1;

        fetchJson(
          fetchPath,
        {
            body: JSON.stringify({...params.request, ...fetchBody, ...{page: page}}),
            method: 'POST'
          }
        ).then (response => {
          if (typeof onResponse == 'function') onResponse();

          if (response){
            params.success({
              rowData: JSONtoArr(response.rows),
              rowCount: response.rowCount,
            });

            if(response.rowCount == 0) {
              params.api.showNoRowsOverlay()
            } else {
              params.api.hideOverlay()
            }

            if (typeof onSuccess == 'function') onSuccess(response, params)

          } else {
            // inform grid request failed
            params.fail();
            params.api.hideOverlay()
          }
        })
      }
    }
  };

}

const ServerSidePaginationAggrid = forwardRef(({
  columnDefs,
  fetchPath,
  externalFilter,
  externalGridRef,
  callbackAfterRequest = () => {},
  callbackOnRowSelected = () => {},
  callbackOnGridChange = () => {},
  onGridReadyCallback,
  defaultGridOptions = {} ,
  style = {},
  clearSelectionsKey,
  refreshBodyChanges = false,
  defaultColumnState = [],
  resizeToRowsHeight,
  theme,
  ...props
}) => {
  const selectedRowsRef = useRef(new Map());
  const gridRef = externalGridRef || useRef(null);
  const gridId = props.id || 'server-side-pagination-grid';
  const [_, getRowId] = useRowId();
  const [loading, setLoading] = useState(false);

  const serverSideDatasource = usePaginatedSSR({
    fetchPath: fetchPath,
    fetchBody: props.fetchBody,
    onResponse: () => { setLoading(false) },
    onSuccess: (response, params) => {
      // Function to update the selected checkboxes based on the selected rows
      updateSelectedRows();
      // Function that invokes a callback function and sends response to it
      callbackAfterRequest(response);
      // Function that invokes a callback and sends the updated parameters of ag-Grid to it
      callbackOnGridChange(params);
      
      resizeGrid();
    }
  })

  const onGridReady = (params) => {
    gridRef.current.api = params.api;
    gridRef.current.api.setGridOption('serverSideDatasource', serverSideDatasource());
    resizeGrid();
    if (defaultColumnState.length > 0) {
      gridRef.current.api.applyColumnState({
        state: defaultColumnState,
        applyOrder: true,
      });
    }
    if (onGridReadyCallback) {
      onGridReadyCallback(params.api);
    }
  };

  const onFilterChanged = (event) => {
    callbackOnGridChange({
      type: 'filterChanged',
      filterModel: event.api.getFilterModel(),
      isAnyFilterPresent: event.api.isAnyFilterPresent(),
      source: event.source,
      api: event.api,
      columnApi: event.columnApi
    });
  };

  const onRowSelected = (event) => {
    const id = event.node.data?.id;
    if (id == null) return

    if (event.node.isSelected())
      selectedRowsRef.current.set(id, event.node.data);
    else
      selectedRowsRef.current.delete(id);

    callbackOnRowSelected(Array.from(selectedRowsRef.current.values()));
  };

  const updateSelectedRows = () => {
    gridRef.current?.api?.forEachNode(node => {
      let isRowSelected = !!(node.data && selectedRowsRef.current.has(node.data.id));
      if (node.id){
        node.setSelected(isRowSelected);
      }
    });
  };

  useEffect(() => {
    if (gridRef.current?.api?.setGridOption && refreshBodyChanges) {
      onGridReady({api: gridRef.current.api});
    }
  }, [JSON.stringify(props.fetchBody)]);

  useEffect(() => {
    if (!externalFilter) return;
    setLoading(true);

    const timer = setTimeout(() => {
      if (gridRef.current.api?.setFilterModel != null) {
        const api = gridRef.current.api;
        const isAdvanced = api.getGridOption('enableAdvancedFilter');

        // Check if the external filter is already a proper Ag‑Grid filter model
        const normalize = 
          val => val && typeof(val) === 'object' && 'filterType' in val
            ? val
            : { filter: val, filterType: 'text', type: 'contains' };

        if (isAdvanced) {
          const colId = externalFilter.colId ?? Object.keys(externalFilter)[0];
          const value = externalFilter.colId ? externalFilter : externalFilter[colId];

          api.setAdvancedFilterModel({
            colId,
            ...normalize(value)
          });
        } else {
          const filterModel = Object.fromEntries(
            Object.entries(externalFilter).map(([key, value]) => [
              key,
              normalize(value)
            ])
          );

          api.setFilterModel(filterModel);
        };

        api.paginationGoToPage(0);
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [externalFilter]);

  useEffect(() => {
    $('#'+gridId).off('click').on('click', '.ag-refresh', function() {
      gridRef.current.api.refreshServerSide({ purge: true });
    });
  }, []);

  useEffect(() => {
    if (clearSelectionsKey !== undefined) {
      selectedRowsRef.current.clear();
    }
  }, [clearSelectionsKey]);

  const resizeGrid = (sizeToFit = false) => {
    if (style.height) return;

    const api = gridRef.current?.api;
    const grid = document.getElementById(gridId);
    if (!api || !grid) return;

    if (sizeToFit) api.sizeColumnsToFit();

    if (!resizeToRowsHeight) {
      grid.style.height = `${window.innerHeight - grid.getBoundingClientRect().top}px`;
      return;
    }

    const maxHeight = window.innerHeight - grid.getBoundingClientRect().top;
    const headerHeight = grid.querySelector('.ag-header')?.getBoundingClientRect().height || 0;

    const pageSize = api.paginationGetPageSize();
    const start = api.paginationGetCurrentPage() * pageSize;
    const rows = Math.min(pageSize, Math.max(0, api.paginationGetRowCount() - start));

    const rowHeight = api.getDisplayedRowAtIndex(0)?.rowHeight || 42;
    const neededHeight = headerHeight + rows * rowHeight;

    api.setGridOption('domLayout', neededHeight <= maxHeight ? 'autoHeight' : 'normal');
    grid.style.height = neededHeight <= maxHeight ? '' : `${maxHeight}px`;
  };

  const gridOptions = useMemo(() => {
    return {
      onGridReady,
      onRowSelected,
      rowModelType: 'serverSide',
      domLayout : 'autoHeight',
      ...(resizeToRowsHeight ? {
        onFirstDataRendered: resizeGrid,
        onModelUpdated: resizeGrid,
        onPaginationChanged: resizeGrid,
      } : {}),
      ...defaultGridOptions,
      ...DATA_TYPE_DEFINITIONS
    };
  }, []);

  useEffect(() => {
    window.addEventListener('resize', () => resizeGrid(true));
    return () => {
        window.removeEventListener('resize', () => resizeGrid(true));
    };
  }, []);

  const loadingOverlayComponent = useMemo(() => {
    return LoadingIndicator;
  }, []);

  // const agColumnDefs = useMemo(() => {
  //   return columnDefs;
  // }, []);

  const defaultColDef = useMemo(() => { 
    return {
          minWidth: 150,
      };
  }, []);

  return (
    <div
      id={gridId}
      className={`grid-theme`}
      style={{
        width: '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        height: 'calc(100vh - 180px)',
        ...style // Spread the incoming style props
      }}
    >
      <AgGridReact theme={theme || gridThemeNoBorder}
        ref={gridRef}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        gridOptions={gridOptions}
        getRowId={getRowId}
        loadingOverlayComponent={loadingOverlayComponent}
        loading={loading}
        getLocaleText={getLocaleText}
        onFilterChanged={onFilterChanged}
        {...commonGridOptions}
      />

      <div className='ag-refresh'/>
    </div>
  );
});

export default ServerSidePaginationAggrid;
